import { gql, useQuery, useReactiveVar } from '@apollo/client';
import React, { useMemo } from 'react';
import { TransferGroupSchemasQuery, TransferGroupSchemasQueryVariables } from '../graphql/types';
import { tokenDataVar } from '../lib/Auth';

export const TRANSFER_GROUP_SCHEMAS = gql`
  query TransferGroupSchemas {
    transferGroupSchemas {
      type
      accountRules {
        directions
        uniqueCount
      }
      rules {
        min
        max
        direction
        type
      }
    }
  }
`;

export type TransferGroupSchemas = TransferGroupSchemasQuery['transferGroupSchemas'];

interface TransferGroupSchemasContextValue {
  loading: boolean;
  transferGroupSchemas: TransferGroupSchemas;
}

const TransferGroupSchemasContext = React.createContext<TransferGroupSchemasContextValue | undefined>(
  undefined,
);

export default function TransferGroupSchemasProvider({ children }: React.PropsWithChildren<object>) {
  const isLoggedIn = Boolean(useReactiveVar(tokenDataVar));
  const { data, loading } = useQuery<TransferGroupSchemasQuery, TransferGroupSchemasQueryVariables>(
    TRANSFER_GROUP_SCHEMAS,
    { fetchPolicy: 'cache-first', skip: !isLoggedIn },
  );
  const transferGroupSchemas = useMemo(() => data?.transferGroupSchemas ?? [], [data?.transferGroupSchemas]);
  const value = useMemo(() => ({ loading, transferGroupSchemas }), [loading, transferGroupSchemas]);

  return (
    <TransferGroupSchemasContext.Provider value={value}>{children}</TransferGroupSchemasContext.Provider>
  );
}

export function useTransferGroupSchemasContext() {
  const context = React.useContext(TransferGroupSchemasContext);
  if (!context) {
    throw new Error('useTransferGroupSchemasContext must be used within TransferGroupSchemasProvider');
  }
  return context;
}
